import { SingleBuildingContext } from "features/dt-basic-setup/single-building/context/AddSingleBuildingContext";
import { STEP } from "features/dt-basic-setup/single-building/model/constants";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { getUserFromLocalStorage } from "utils/localStorage";

import { useAddressStep } from "../../address-step/hooks/useAddressStep";
import { createSubmitAction } from "../lib/createSubmitAction";
import { useCallJobStatusEffect } from "./useCallJobStatusEffect";
import { useDeleteImages } from "./useDeleteImages";
import { useModalActionButton } from "./useModalActionButton";
import { useSubmitInfoState } from "./useSubmitInfoState";
import { useUploadAndSubmit } from "./useUploadAndSubmit";

export const useHandleSubmit = (projectId: string | undefined) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userId = getUserFromLocalStorage().user_id ?? "";
  const clickedButtonRef = useRef<"saveAndExit" | "submit" | undefined>(undefined);

  const { isEpcExtractionAvailable } = useAddressStep();

  const { canExecuteDTCreation, formData, jobId, setCanExecuteDTCreation, setJobId, stepNo } =
    useContext(SingleBuildingContext);

  const { dispatch, submitInfo, submitInfoDefault } = useSubmitInfoState({ formData, projectId });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitModalOpen = isSubmitting && stepNo < STEP.final;

  useEffect(() => {
    if (stepNo >= STEP.final) setIsSubmitting(false);
  }, [stepNo]);

  const shouldAbortSubmitCall = useCallback(() => {
    if (jobId || !canExecuteDTCreation) {
      return true;
    }
  }, [canExecuteDTCreation, jobId]);

  const deleteImages = useDeleteImages(projectId, dispatch);

  const { cancelInFlightUploads, handleSubmitBase } = useUploadAndSubmit({
    deleteImages,
    dispatch,
    setIsSubmitting,
    setJobId,
    shouldAbortSubmitCall,
    submitInfoDefault,
    userId,
  });

  const { modalActionButton } = useModalActionButton({
    cancelInFlightUploads,
    isSubmitting,
    setIsSubmitting,
    submitInfo,
  });

  const actionFactory = useCallback(
    (isSaveAndExit: boolean) =>
      createSubmitAction({ handleSubmitBase, isEpcExtractionAvailable, isSaveAndExit, projectId, t }),
    [handleSubmitBase, isEpcExtractionAvailable, projectId, t]
  );

  const handleSaveAndExit = () => {
    clickedButtonRef.current = "saveAndExit";
    actionFactory(true).action();
  };

  const handleSubmit = () => {
    clickedButtonRef.current = "submit";
    actionFactory(false).action();
  };

  const onJobCompleted = useMemo(
    () => (completedProjectId: string) => {
      setCanExecuteDTCreation(false);
      queryClient.invalidateQueries();
      const action = actionFactory(clickedButtonRef.current === "saveAndExit");
      const navigateUrl = action.getNavigateUrl(completedProjectId);
      if (navigateUrl) navigate(navigateUrl);
    },
    [actionFactory, navigate, queryClient, setCanExecuteDTCreation]
  );

  useCallJobStatusEffect({
    dispatch,
    isSubmitting,
    jobId,
    onJobCompleted,
    setIsSubmitting,
  });

  return {
    handleSaveAndExit,
    handleSubmit,
    isSubmitting,
    modalActionButton,
    submitInfo,
    submitModalOpen,
  };
};
